import {
  Box,
  Modal,
  ModalCloseButton,
  ModalContent,
  Theme,
  Typography,
} from "@outschool/backpack";
import { Activity } from "@outschool/gql-backend-generated";
import {
  PastTeachersForParentProfileQuery,
  SavedClassListQueryQuery,
} from "@outschool/gql-frontend-generated";
import { useLocalPriceDisplay, useTranslation } from "@outschool/localization";
import { Activity_PricingFragment } from "@outschool/localization/src/useLocalPriceDisplay/Pricing";
import { cadenceShortString } from "@outschool/time";
import React, { useState } from "react";

import SunBackground from "../../../images/SunBackground.svg";
import SimpleTeacherCard from "../../Teachers/SimpleTeacherCard";
import { CopyOutreachLinkButton } from "./CopyOutreachLinkButton";
import { OutreachContent } from "./OutreachContent";
import { TeacherVideoModal } from "../../TeacherVideoModal";
import SectionCardContent, {
  ActivityCardContentType,
  SectionCardContentType,
} from "./SectionCardContent";

export enum OutreachModalType {
  favorites,
  teacher,
  class,
  fallback,
}

export type SharingFavoritesType = SavedClassListQueryQuery["savedClassList"];

export type SharingActivityType = Omit<
  ActivityCardContentType,
  "leader" | "details"
> & {
  leader?: Omit<
    ActivityCardContentType["leader"],
    "reviewCount" | "averageActivityStarRating"
  > & {
    reviewCount?: number;
    averageActivityStarRating?: number | null;
  };
  details?: ActivityCardContentType["details"];
} & Omit<Activity_PricingFragment, "size_min"> &
  Pick<Activity, "duration_minutes">;

export type SharingSectionType = Omit<SectionCardContentType, "leader"> & {
  leader?: Omit<
    SectionCardContentType["leader"],
    "reviewCount" | "averageActivityStarRating"
  > & {
    reviewCount?: number;
    averageActivityStarRating?: number | null;
  };
};

type OutreachModalProps = {
  open: boolean;
  onClose: () => void;
  outreachShareUrl: string;
  modalType:
    | {
        variant: OutreachModalType.favorites;
        favoritesList: SharingFavoritesType;
      }
    | {
        variant: OutreachModalType.teacher;
        teacher: PastTeachersForParentProfileQuery["pastTeachersForParentProfile"][0];
      }
    | {
        variant: OutreachModalType.class;
        activity?: SharingActivityType;
        section?: SharingSectionType;
      }
    | {
        variant: OutreachModalType.fallback;
      };
};

export function OutreachModal({
  open,
  onClose,
  outreachShareUrl,
  modalType,
}: OutreachModalProps) {
  return (
    <Modal open={open} onClose={onClose}>
      <ModalContent
        sx={(theme: Theme) => ({
          display: "flex",
          flexDirection: "column",
          position: "relative",
          padding: "0px",
          color: "primary.700",
          background: "linear-gradient(to bottom, #F0F3FF 25%, white 50%)",
          backgroundAttachment: "local",
          [theme.breakpoints.up("md")]: {
            flexDirection: "row",
            background: "linear-gradient(to right, #F0F3FF 25%, white 50%)",
            minWidth: "600px",
            minHeight: "400px",
            height:
              modalType.variant === OutreachModalType.class ? "480px" : "400px",
          },
        })}
      >
        <ModalCloseButton
          aria-label="close"
          onClick={onClose}
          size="large"
          variant="text"
          sx={{
            position: "absolute",
            top: "1.5rem",
            left: "1.5rem",
            margin: 0,
            color: "primary.700",
            fontSize: "2.5rem",
          }}
        />

        {/* LEFT/TOP HALF */}
        <Box
          sx={(theme: Theme) => ({
            paddingTop: "3rem",
            backgroundColor: "#F0F3FF",
            marginTop: "6rem",
            padding: "2rem",
            height: "100%",
            display: "flex",
            flexDirection: "column-reverse",
            paddingBottom: "2rem",
            [theme.breakpoints.up("md")]: {
              marginTop: 0,
              width: "50%",
              paddingTop: "0rem",
            },
          })}
        >
          <OutreachContent />
        </Box>

        {/* Skip right half if falling back or missing activity */}
        {modalType.variant === OutreachModalType.fallback ||
        (modalType.variant === OutreachModalType.class &&
          !modalType.activity) ? null : (
          /* RIGHT/BOTTOM HALF */
          <Box
            sx={(theme: Theme) => ({
              width: "calc(100%)",
              margin: 0,
              padding: 0,
              backgroundImage: `url("${SunBackground}")`,
              backgroundSize: "686px 635px",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "0px 120px",
              height: "100%",
              [theme.breakpoints.up("md")]: {
                width: "50%",
                // margin: "auto",
                backgroundPosition:
                  modalType.variant === OutreachModalType.class
                    ? "-60px 320px"
                    : "-60px 190px",
              },
            })}
          >
            <Box
              sx={{
                width: "calc(100% - 4rem)",
                height: "calc(100% - 4rem)",
                margin: "2rem 2rem 2rem 2rem",
                padding: "0px",
              }}
            >
              {modalType.variant === OutreachModalType.favorites && (
                <OutreachModalRightHalfFavorites
                  favoritesList={modalType.favoritesList}
                  outreachShareUrl={outreachShareUrl}
                />
              )}
              {modalType.variant === OutreachModalType.teacher && (
                <OutreachModalRightHalfTeacher
                  teacher={modalType.teacher}
                  outreachShareUrl={outreachShareUrl}
                />
              )}
              {modalType.variant === OutreachModalType.class && (
                <OutreachModalRightHalfClass
                  activity={modalType.activity!}
                  section={modalType.section}
                  outreachShareUrl={outreachShareUrl}
                />
              )}
            </Box>
          </Box>
        )}
      </ModalContent>
    </Modal>
  );
}

function OutreachModalRightHalfFavorites({
  favoritesList,
  outreachShareUrl,
}: {
  favoritesList: SharingFavoritesType;
  outreachShareUrl: string;
}) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\Outreach\\OutreachModal"
  );

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          my: "1rem",
          fontWeight: "bold",
          textAlign: "center",
        }}
      >{t`Share Your Favorites`}</Typography>
      <Box
        display="flex"
        sx={{
          width: "100%",
          justifyContent: "center",
        }}
      >
        <Box
          flex
          sx={{
            width: "160px",
            maxWidth: "160px",
            flexDirection: "column",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          <ImageCard
            backgroundImagePath={
              favoritesList?.listings?.[0]?.activity?.details?.photo ?? ""
            }
          />
          <Typography
            variant="h6"
            sx={{
              width: "100%",
              fontSize: "18px",
              color: "black",
              mt: "1rem",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "center",
            }}
          >
            {favoritesList?.name ?? t("Your Favorites")}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ marginTop: "2rem" }}>
        <CopyOutreachLinkButton outreachShareUrl={outreachShareUrl} />
      </Box>
    </Box>
  );
}
function OutreachModalRightHalfTeacher({
  teacher,
  outreachShareUrl,
}: {
  teacher: PastTeachersForParentProfileQuery["pastTeachersForParentProfile"][0];
  outreachShareUrl: string;
}) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\Outreach\\OutreachModal"
  );

  const [showModal, setShowModal] = useState(false);
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          my: "1rem",
          fontWeight: "bold",
          textAlign: "center",
        }}
      >{t`Share This Profile`}</Typography>
      <SimpleTeacherCard
        teacher={teacher}
        onWatchVideo={() => setShowModal(true)}
      />
      <Box sx={{ marginTop: "2rem" }}>
        <CopyOutreachLinkButton outreachShareUrl={outreachShareUrl} />
      </Box>

      <TeacherVideoModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        teacher={teacher}
      />
    </Box>
  );
}

function OutreachModalRightHalfClass({
  activity,
  section,
  outreachShareUrl,
}: {
  activity: SharingActivityType;
  section?: SharingSectionType;
  outreachShareUrl: string;
}) {
  const { t } = useTranslation(
    "ui-components-website\\Sharing\\Outreach\\OutreachModal"
  );

  const { makePerMeetingPriceText, makePerWeekPriceText } =
    useLocalPriceDisplay();
  const price = activity.isSelfPaced
    ? makePerWeekPriceText(activity)
    : makePerMeetingPriceText(activity);

  const {
    duration_minutes: durationMinutes,
    duration_weeks: durationWeeks,
    weekly_meetings: timesPerWeek,
  } = activity;

  const formattedCadenceString = cadenceShortString({
    durationWeeks,
    timesPerWeek,
    durationMinutes,
    t,
  });

  const sectionWithReviews: SectionCardContentType | undefined = !!section
    ? {
        ...section,
        leader: {
          uid: section?.leader?.uid || activity?.leader?.uid || "",
          name: section?.leader?.name || activity?.leader?.name || null,
          photo: section?.leader?.photo || activity?.leader?.photo || null,
          reviewCount:
            section?.leader?.reviewCount || activity?.leader?.reviewCount || 0,
          averageActivityStarRating:
            section?.leader?.averageActivityStarRating ||
            activity?.leader?.averageActivityStarRating ||
            null,
        },
      }
    : undefined;

  const activityLeader: ActivityCardContentType["leader"] = {
    uid: activity?.leader?.uid || "",
    name: activity?.leader?.name || null,
    photo: activity?.leader?.photo || null,
    ...activity?.leader,
  };

  const sharableActivity: ActivityCardContentType = {
    ...activity,
    leader: activityLeader,
    details: {
      photo: null,
      ...activity.details,
    },
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-around",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: 18,
          fontWeight: "bold",
          textAlign: "center",
        }}
      >{t`Share This Class`}</Typography>
      <Box sx={{ my: "3rem" }}>
        <SectionCardContent
          section={sectionWithReviews}
          activity={sharableActivity}
          price={price}
          formattedCadenceString={formattedCadenceString}
          formattedDurationString={""}
        />
      </Box>
      <Box
        sx={(theme: Theme) => ({
          [theme.breakpoints.down("md")]: {
            marginTop: "2rem",
          },
        })}
      >
        <CopyOutreachLinkButton outreachShareUrl={outreachShareUrl} />
      </Box>
    </Box>
  );
}

function ImageCard({ backgroundImagePath }: { backgroundImagePath: string }) {
  return (
    <Box
      sx={[
        {
          width: "160px",
          height: "88px",
          padding: "24px",
          borderRadius: "16px",
          cursor: "pointer",
          boxShadow:
            "0px 4px 20px rgba(0, 0, 0, 0.05), 0px 56px 53px -43px rgba(0, 0, 0, 0.1)",
          backgroundImage: `url("${backgroundImagePath}")`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        },
      ]}
    />
  );
}
